<script lang="ts">
  import Navbar from "$lib/components/Navbar.svelte";
  import { auth } from "$lib/stores/auth.svelte";
  import { api } from "$lib/api/client";

  // ── Criar workspace ────────────────────────────────────────────────
  let creating = $state(false);
  let name = $state("");
  let type = $state<"personal" | "family" | "business">("personal");

  async function create(e: Event) {
    e.preventDefault();
    creating = true;
    try {
      await api("/workspaces", { method: "POST", body: JSON.stringify({ name, type, plan: "free" }) });
      name = "";
      await auth.bootstrap();
    } finally { creating = false; }
  }

  // ── Membros ────────────────────────────────────────────────────────
  interface Member { id: string; user_id: string; role: string; user_email: string | null; user_name: string | null; }

  let selectedWsId = $state<string | null>(null);
  let members = $state<Member[]>([]);
  let membersLoading = $state(false);
  let memberError = $state("");
  let inviteEmail = $state("");
  let inviteRole = $state("member");
  let inviting = $state(false);

  const selectedWs = $derived(auth.workspaces.find(w => w.id === selectedWsId));
  const isOwner = $derived(selectedWs?.role === "owner");
  // showMembers: inline no template agora (verificado por workspace individual)

  async function loadMembers(wsId: string) {
    membersLoading = true;
    memberError = "";
    try {
      members = await api(`/workspaces/${wsId}/members`) as Member[];
    } catch {
      memberError = "Erro ao carregar membros.";
    } finally {
      membersLoading = false;
    }
  }

  function selectWs(id: string) {
    auth.selectWorkspace(id);
    selectedWsId = id;
    members = [];
    inviteEmail = "";
    const ws = auth.workspaces.find(w => w.id === id);
    if (ws && (ws.type === "family" || ws.type === "business")) {
      loadMembers(id);
    }
  }

  async function invite(e: Event) {
    e.preventDefault();
    if (!selectedWsId) return;
    inviting = true;
    memberError = "";
    try {
      await api(`/workspaces/${selectedWsId}/members`, {
        method: "POST",
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });
      inviteEmail = "";
      await loadMembers(selectedWsId);
    } catch (err: any) {
      memberError = err?.detail ?? "Erro ao convidar membro.";
    } finally {
      inviting = false;
    }
  }

  async function removeMember(userId: string) {
    if (!selectedWsId || !confirm("Remover este membro?")) return;
    try {
      await api(`/workspaces/${selectedWsId}/members/${userId}`, { method: "DELETE" });
      await loadMembers(selectedWsId);
    } catch (err: any) {
      memberError = err?.detail ?? "Erro ao remover membro.";
    }
  }

  const ROLE_LABELS: Record<string, string> = { owner: "Dono", admin: "Admin", member: "Membro" };
</script>

<Navbar />
<main class="max-w-3xl mx-auto px-4 py-6 space-y-6">
  <h1 class="text-2xl font-bold">Workspaces</h1>

  <!-- ── Lista de workspaces ───────────────────────────────────────── -->
  <div class="card space-y-2">
    <h2 class="font-semibold">Seus workspaces</h2>
    <ul class="divide-y divide-slate-200 dark:divide-slate-700">
      {#each auth.workspaces as w}
        {@const isSelected = selectedWsId === w.id}
        <li class="py-3 space-y-3">
          <!-- cabeçalho da linha -->
          <div class="flex items-center justify-between">
            <div>
              <span class="font-medium">{w.name}</span>
              <span class="ml-2 text-xs text-slate-400 capitalize">{w.type} · {w.plan} · {ROLE_LABELS[w.role] ?? w.role}</span>
            </div>
            <div class="flex gap-2 items-center">
              {#if w.type === "family" || w.type === "business"}
                <button
                  class="text-xs px-3 py-1 rounded-full border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  onclick={() => isSelected ? selectedWsId = null : selectWs(w.id)}
                >
                  {isSelected ? "Fechar" : "👥 Membros"}
                </button>
              {/if}
              <button class="btn-primary text-sm py-1 px-3" onclick={() => auth.selectWorkspace(w.id)}>
                Selecionar
              </button>
            </div>
          </div>

          <!-- painel de membros (inline, abaixo do workspace) -->
          {#if isSelected && (w.type === "family" || w.type === "business")}
            <div class="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4 space-y-4">

              {#if memberError}
                <p class="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 rounded p-2">{memberError}</p>
              {/if}

              <!-- lista de membros -->
              {#if membersLoading}
                <p class="text-sm text-slate-400">Carregando membros…</p>
              {:else if members.length === 0}
                <p class="text-sm text-slate-400">Nenhum membro além de você ainda.</p>
              {:else}
                <ul class="space-y-2">
                  {#each members as m}
                    <li class="flex items-center justify-between bg-white dark:bg-slate-700 rounded-lg px-3 py-2">
                      <div>
                        <div class="text-sm font-medium">{m.user_name ?? m.user_email ?? "—"}</div>
                        {#if m.user_name && m.user_email}
                          <div class="text-xs text-slate-400">{m.user_email}</div>
                        {/if}
                      </div>
                      <div class="flex items-center gap-3">
                        <span class="text-xs px-2 py-0.5 rounded-full
                          {m.role === 'owner' ? 'bg-brand/10 text-brand' : 'bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300'}">
                          {ROLE_LABELS[m.role] ?? m.role}
                        </span>
                        {#if isOwner && m.role !== "owner"}
                          <button
                            class="text-xs text-red-500 hover:text-red-700 hover:underline"
                            onclick={() => removeMember(m.user_id)}
                          >Remover</button>
                        {/if}
                      </div>
                    </li>
                  {/each}
                </ul>
              {/if}

              <!-- formulário de convite (apenas owner) -->
              {#if isOwner}
                <form onsubmit={invite} class="space-y-2 border-t border-slate-200 dark:border-slate-700 pt-3">
                  <p class="text-xs font-semibold text-slate-500 uppercase tracking-wide">Adicionar membro</p>
                  <input
                    class="input w-full"
                    type="email"
                    bind:value={inviteEmail}
                    placeholder="email do novo membro"
                    required
                  />
                  <div class="flex gap-2">
                    <select class="input w-36" bind:value={inviteRole}>
                      <option value="member">Membro</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button class="btn-primary flex-1" disabled={inviting}>
                      {inviting ? "Adicionando…" : "Adicionar"}
                    </button>
                  </div>
                </form>
              {/if}

            </div>
          {/if}
        </li>
      {/each}
    </ul>
  </div>

  <!-- ── Criar novo workspace ───────────────────────────────────────── -->
  <form onsubmit={create} class="card space-y-3">
    <h2 class="font-semibold">Criar novo workspace</h2>
    <input class="input" bind:value={name} placeholder="Nome do workspace" required />
    <select class="input" bind:value={type}>
      <option value="personal">Pessoal</option>
      <option value="family">Familiar</option>
      <option value="business">Empresarial</option>
    </select>
    <button class="btn-primary" disabled={creating}>{creating ? "Criando…" : "Criar"}</button>
  </form>
</main>
